/**
 * Cross-tab activity synchronization for session idle tracking.
 * 
 * Uses industry-standard pattern (Okta/Auth0 style):
 * - localStorage for cross-window persistence
 * - BroadcastChannel for fast same-origin sync
 * - Throttled updates to prevent performance issues
 */

const ACTIVITY_KEY = 'experience:last-activity';
const CHANNEL_NAME = 'experience-activity';

let bc: BroadcastChannel | null = null;

function getBroadcastChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined' || !('BroadcastChannel' in window)) {
    return null;
  }
  if (!bc) {
    bc = new BroadcastChannel(CHANNEL_NAME);
  }
  return bc;
}

function throttle<T extends (...args: unknown[]) => void>(
  fn: T,
  delayMs: number
): T {
  let lastCall = 0;
  return ((...args: unknown[]) => {
    const now = Date.now();
    if (now - lastCall >= delayMs) {
      lastCall = now;
      fn(...args);
    }
  }) as T;
}

/**
 * Get the last recorded activity timestamp across all tabs.
 */
export function getLastActivity(): number {
  try {
    return parseInt(localStorage.getItem(ACTIVITY_KEY) || '0', 10);
  } catch {
    return 0;
  }
}

/**
 * Record activity in this tab and broadcast to others.
 */
export function recordActivity(): void {
  const now = Date.now();
  try {
    localStorage.setItem(ACTIVITY_KEY, String(now));
  } catch {
    // localStorage may be unavailable (private browsing, quota exceeded)
  }
  try {
    getBroadcastChannel()?.postMessage({ type: 'activity', ts: now });
  } catch {
    // BroadcastChannel may fail
  }
}

/**
 * Start tracking user activity in this tab.
 * Returns cleanup function.
 */
export function startActivityTracking(): () => void {
  const throttledRecord = throttle(recordActivity, 1000);

  const events: Array<keyof WindowEventMap> = [
    'mousemove',
    'keydown',
    'click',
    'scroll',
    'touchstart',
    'pointerdown',
  ];

  events.forEach((event) => {
    window.addEventListener(event, throttledRecord, { passive: true });
  });

  // Record initial activity
  recordActivity();

  return () => {
    events.forEach((event) => {
      window.removeEventListener(event, throttledRecord);
    });
  };
}

/**
 * Subscribe to activity events from other tabs.
 * Returns cleanup function.
 */
export function onCrossTabActivity(callback: () => void): () => void {
  // localStorage 'storage' event fires in OTHER tabs when localStorage changes
  const storageHandler = (e: StorageEvent) => {
    if (e.key === ACTIVITY_KEY && e.newValue) {
      callback();
    }
  };

  // BroadcastChannel fires in all tabs including sender's other contexts
  const channel = getBroadcastChannel();
  const bcHandler = (e: MessageEvent) => {
    if (e.data?.type === 'activity') {
      callback();
    }
  };

  window.addEventListener('storage', storageHandler);
  channel?.addEventListener('message', bcHandler);

  return () => {
    window.removeEventListener('storage', storageHandler);
    channel?.removeEventListener('message', bcHandler);
  };
}

/**
 * Check if there's been recent activity (within the given threshold).
 * Useful for periodic checks as a fallback.
 */
export function hasRecentActivity(thresholdMs: number): boolean {
  const lastActivity = getLastActivity();
  return Date.now() - lastActivity < thresholdMs;
}









